import React, { useEffect, useState } from 'react';
import {
  X,
  Send,
  Play,
  Eye,
  StickyNote,
  Download,
  Loader2,
  AlertTriangle,
  RotateCcw,
  CheckCircle2,
  ListFilter,
  HelpCircle,
  Calendar,
  AlertCircle,
} from 'lucide-react';
import Modal from '../../shared/Modal';
import DocPreviewModal from '../../shared/DocPreviewModal';
import type { EffectLine } from './phaseActionEffects';
import type {
  ProgramPhaseActionDef,
  ProgramPhaseActionInstance,
  PhaseActionSelectOption,
} from '../../../types/programPhases';

/**
 * The three popups of the drawer Admin Tasks one-click controls
 * (Part 3 of ADMIN_TASK_FLOW_AND_DRAWER_SPEC, 2026-10-25):
 *
 *  - SendConfirmModal: shown BEFORE any send-type phase action
 *    executes. Nothing runs until its Confirm click.
 *  - ContentViewModal: renders a display task's content (visual_text
 *    display text, visual_file reference file), a completed note, or
 *    an uploaded file — readable typography, sized to content within
 *    the viewport, internal scrolling when long.
 *  - NoteEditModal: the text_input editor; saving writes the entry
 *    through the existing completion flow (the caller does that).
 *
 * All light theme, all built on the shared Modal (portal-rendered,
 * never clipped by the drawer's overflow).
 */

function ModalShell({
  onClose,
  labelId,
  icon,
  title,
  subtitle,
  children,
  wide,
}: {
  onClose: () => void;
  labelId: string;
  icon: React.ReactNode;
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  wide?: boolean;
}) {
  return (
    <Modal onClose={onClose} labelId={labelId}>
      <div
        className={`bg-white rounded-2xl shadow-2xl w-full ${wide ? 'max-w-2xl' : 'max-w-md'} max-h-[88vh] flex flex-col overflow-hidden border border-slate-200`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 px-5 pt-4 pb-3 border-b border-slate-100 shrink-0">
          <div className="min-w-0">
            <h3 id={labelId} className="text-sm font-extrabold text-slate-900 flex items-center gap-2">
              {icon}
              <span className="truncate">{title}</span>
            </h3>
            {subtitle && <p className="text-xs text-slate-500 mt-0.5">{subtitle}</p>}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-lg cursor-pointer shrink-0"
            aria-label="Close"
          >
            <X size={16} />
          </button>
        </div>
        {children}
      </div>
    </Modal>
  );
}

// ── 3.1 — Send confirmation ─────────────────────────────────────────

export interface SendConfirmData {
  taskTitle: string;
  kind: 'send_automation' | 'send_email';
  /** Bound automation / template name shown as the headline item. */
  boundName?: string;
  applicantName?: string;
  recipientEmail?: string;
  effects: EffectLine[];
  /** Non-fatal warnings (e.g. binding could not be resolved). */
  warnings: string[];
}

export function SendConfirmModal({
  data,
  loading,
  loadError,
  confirming,
  onConfirm,
  onCancel,
}: {
  data: SendConfirmData | null;
  loading: boolean;
  loadError: string | null;
  confirming: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <ModalShell
      onClose={onCancel}
      labelId="phase-action-confirm-title"
      icon={data?.kind === 'send_automation' ? <Play size={17} className="text-indigo-600" /> : <Send size={17} className="text-indigo-600" />}
      title={data ? `Confirm: ${data.taskTitle}` : 'Confirm send'}
      subtitle={
        data && (data.applicantName || data.recipientEmail)
          ? `To ${[data.applicantName, data.recipientEmail ? `<${data.recipientEmail}>` : ''].filter(Boolean).join(' ')}`
          : undefined
      }
    >
      <div className="px-5 py-4 overflow-y-auto">
        {loading && (
          <p className="flex items-center gap-2 text-[13px] text-slate-500">
            <Loader2 size={14} className="animate-spin" /> Reading what this will do…
          </p>
        )}
        {!loading && loadError && (
          <p className="flex items-start gap-2 text-[13px] text-rose-700 bg-rose-50 border border-rose-200 rounded-xl px-3 py-2">
            <AlertTriangle size={14} className="shrink-0 mt-0.5" /> {loadError}
          </p>
        )}
        {!loading && !loadError && data && (
          <>
            <p className="text-[13px] text-slate-600">
              {data.kind === 'send_automation' ? (
                <>Running <strong>{data.boundName || 'the selected automation'}</strong> will:</>
              ) : (
                <>Sending <strong>{data.boundName || 'the selected email'}</strong> will:</>
              )}
            </p>
            <ul className="mt-2.5 space-y-1.5">
              {data.effects.map((line, i) => (
                <li key={i} className="flex items-start gap-2 text-[13px] text-slate-700">
                  <span className="mt-[7px] h-1 w-1 rounded-full bg-indigo-400 shrink-0" aria-hidden="true" />
                  <span className={line.bold ? 'font-extrabold text-slate-900' : undefined}>{line.text}</span>
                </li>
              ))}
            </ul>
            {data.warnings.map((w, i) => (
              <p key={i} className="mt-3 flex items-start gap-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2">
                <AlertTriangle size={13} className="shrink-0 mt-0.5" /> {w}
              </p>
            ))}
            <p className="mt-3 text-xs text-slate-500">
              The task is only marked complete if this succeeds. If it fails, the task stays incomplete and the reason is shown on the task.
            </p>
          </>
        )}
      </div>
      <div className="px-5 py-3 border-t border-slate-100 flex items-center justify-end gap-2 shrink-0">
        <button
          type="button"
          onClick={onCancel}
          disabled={confirming}
          className="px-3.5 py-2 rounded-xl border border-slate-200 text-slate-600 text-xs font-bold hover:bg-slate-50 disabled:opacity-50 cursor-pointer"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={onConfirm}
          disabled={loading || !!loadError || confirming}
          className="px-4 py-2 rounded-xl bg-brand-primary text-white text-xs font-bold hover:bg-brand-primary-dark disabled:opacity-50 cursor-pointer inline-flex items-center gap-1.5"
        >
          {confirming ? <Loader2 size={13} className="animate-spin" /> : data?.kind === 'send_automation' ? <Play size={13} /> : <Send size={13} />}
          {confirming ? 'Running…' : data?.kind === 'send_automation' ? 'Run it' : 'Send it'}
        </button>
      </div>
    </ModalShell>
  );
}

// ── 3.2 — Retrigger confirmation (re-send / re-run) ─────────────────

export interface RetriggerConfirmData {
  taskTitle: string;
  kind: 'email' | 'automation';
  /** What will be sent — the template / automation name. */
  boundName?: string;
  applicantName?: string;
  recipientEmail?: string;
  /** Why the previous run failed, when this re-send follows a failure. */
  previousError?: string;
}

export function RetriggerConfirmModal({
  data,
  confirming,
  onConfirm,
  onCancel,
}: {
  data: RetriggerConfirmData;
  confirming: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const isEmail = data.kind === 'email';
  return (
    <ModalShell
      onClose={onCancel}
      labelId="phase-action-retrigger-title"
      icon={isEmail ? <Send size={17} className="text-indigo-600" /> : <Play size={17} className="text-indigo-600" />}
      title={isEmail ? `Re-send: ${data.taskTitle}` : `Re-run: ${data.taskTitle}`}
      subtitle={
        data.applicantName || data.recipientEmail
          ? `To ${[data.applicantName, data.recipientEmail ? `<${data.recipientEmail}>` : ''].filter(Boolean).join(' ')}`
          : undefined
      }
    >
      <div className="px-5 py-4 overflow-y-auto">
        <p className="text-[13px] text-slate-600">
          {isEmail ? (
            <>This queues <strong>{data.boundName || 'the bound email'}</strong> to be sent again{data.recipientEmail ? <> to <strong>{data.recipientEmail}</strong></> : ''}.</>
          ) : (
            <>This queues <strong>{data.boundName || 'the bound automation'}</strong> to run again for this applicant.</>
          )}
        </p>
        {data.previousError && (
          <p className="mt-3 flex items-start gap-2 text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-xl px-3 py-2">
            <AlertTriangle size={13} className="shrink-0 mt-0.5" />
            <span>The last run failed: {data.previousError}</span>
          </p>
        )}
        <p className="mt-3 text-xs text-slate-500">
          The task stays completed — re-sending never un-completes it. The {isEmail ? 'send' : 'run'} happens in the
          background, so you can keep working; its status updates on the task.
        </p>
      </div>
      <div className="px-5 py-3 border-t border-slate-100 flex items-center justify-end gap-2 shrink-0">
        <button
          type="button"
          onClick={onCancel}
          disabled={confirming}
          className="px-3.5 py-2 rounded-xl border border-slate-200 text-slate-600 text-xs font-bold hover:bg-slate-50 disabled:opacity-50 cursor-pointer"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={onConfirm}
          disabled={confirming}
          className="px-4 py-2 rounded-xl bg-brand-primary text-white text-xs font-bold hover:bg-brand-primary-dark disabled:opacity-50 cursor-pointer inline-flex items-center gap-1.5"
        >
          {confirming ? <Loader2 size={13} className="animate-spin" /> : isEmail ? <Send size={13} /> : <Play size={13} />}
          {confirming ? 'Queuing…' : isEmail ? 'Re-send' : 'Re-run'}
        </button>
      </div>
    </ModalShell>
  );
}

// ── Universal Task Action Confirmation Modal ────────────────────────

export interface TaskConfirmModalProps {
  instance: ProgramPhaseActionInstance;
  def?: ProgramPhaseActionDef;
  applicantName?: string;
  recipientEmail?: string;
  confirming: boolean;
  emailTemplateNames?: Map<string, string>;
  automationNames?: Map<string, string>;
  onConfirm: (payload: {
    selectedOptionId?: string;
    selectedOptionLabel?: string;
    savedAnswer?: string;
    savedDate?: string;
    notes?: string;
    savedText?: string;
  }) => void;
  onCancel: () => void;
}

export function ActionTaskConfirmModal({
  instance,
  def,
  applicantName,
  recipientEmail,
  confirming,
  emailTemplateNames,
  automationNames,
  onConfirm,
  onCancel,
}: TaskConfirmModalProps) {
  const isRequireNote = instance.requireNote || def?.requireNote || def?.config?.requireNote || false;
  const [selectedOptId, setSelectedOptId] = useState<string>(
    def?.config?.options?.[0]?.id || ''
  );
  const [yesNoAnswer, setYesNoAnswer] = useState<'yes' | 'no'>('yes');
  const [dateValue, setDateValue] = useState<string>(
    new Date().toISOString().split('T')[0]
  );
  const [noteText, setNoteText] = useState<string>('');
  const [validationError, setValidationError] = useState<string | null>(null);

  const options: PhaseActionSelectOption[] = def?.config?.options || [];
  const selectedOption = options.find((o) => o.id === selectedOptId);

  const handleConfirm = () => {
    setValidationError(null);

    if (isRequireNote && !noteText.trim()) {
      setValidationError('A note is mandatory for this action. Please enter a note before confirming.');
      return;
    }

    if (instance.actionType === 'select_one_option') {
      if (!selectedOptId || !selectedOption) {
        setValidationError('Please select one option to proceed.');
        return;
      }
      onConfirm({
        selectedOptionId: selectedOption.id,
        selectedOptionLabel: selectedOption.label,
        notes: noteText.trim(),
        savedText: noteText.trim(),
      });
      return;
    }

    if (instance.actionType === 'yes_no') {
      const label = yesNoAnswer === 'yes' ? (def?.config?.yesLabel || 'Yes') : (def?.config?.noLabel || 'No');
      onConfirm({
        savedAnswer: yesNoAnswer,
        notes: noteText.trim(),
        savedText: `Answer: ${label}${noteText.trim() ? ` — ${noteText.trim()}` : ''}`,
      });
      return;
    }

    if (instance.actionType === 'date_entry') {
      if (def?.config?.requireDate !== false && !dateValue) {
        setValidationError('Please select or enter a valid date.');
        return;
      }
      onConfirm({
        savedDate: dateValue,
        notes: noteText.trim(),
        savedText: `Date: ${dateValue}${noteText.trim() ? ` — ${noteText.trim()}` : ''}`,
      });
      return;
    }

    // Default / legacy fallback completion confirmation
    onConfirm({
      notes: noteText.trim(),
      savedText: noteText.trim() || undefined,
    });
  };

  const renderIcon = () => {
    if (instance.actionType === 'select_one_option') return <ListFilter size={18} className="text-indigo-600" />;
    if (instance.actionType === 'yes_no') return <HelpCircle size={18} className="text-indigo-600" />;
    if (instance.actionType === 'date_entry') return <Calendar size={18} className="text-indigo-600" />;
    return <CheckCircle2 size={18} className="text-indigo-600" />;
  };

  return (
    <ModalShell
      onClose={onCancel}
      labelId="task-action-confirm-title"
      icon={renderIcon()}
      title={`Confirm: ${instance.title}`}
      subtitle={
        applicantName || recipientEmail
          ? `For applicant ${[applicantName, recipientEmail ? `<${recipientEmail}>` : ''].filter(Boolean).join(' ')}`
          : 'Please review and confirm this task action.'
      }
      wide={instance.actionType === 'select_one_option'}
    >
      <div className="px-5 py-4 space-y-4 overflow-y-auto">
        {instance.instructions && (
          <div className="p-3 rounded-xl bg-slate-50 border border-slate-200 text-[13px] text-slate-600">
            <strong>Instructions:</strong> {instance.instructions}
          </div>
        )}

        {validationError && (
          <div className="p-3 rounded-xl bg-rose-50 border border-rose-200 text-[13px] font-semibold text-rose-700 flex items-center gap-2">
            <AlertTriangle size={14} className="shrink-0" />
            <span>{validationError}</span>
          </div>
        )}

        {/* 1. Select One Option Inputs */}
        {instance.actionType === 'select_one_option' && (
          <div className="space-y-3">
            <label className="block text-xs font-extrabold text-slate-800 uppercase tracking-wider">
              Choose One Outcome / Action *
            </label>
            <div className="space-y-2">
              {options.map((opt) => {
                const isSelected = opt.id === selectedOptId;
                const boundEmailName = opt.emailTemplateId ? emailTemplateNames?.get(opt.emailTemplateId) : null;
                const boundAutoName = opt.automationId ? automationNames?.get(opt.automationId) : null;

                return (
                  <label
                    key={opt.id}
                    onClick={() => setSelectedOptId(opt.id)}
                    className={`block p-3.5 rounded-xl border cursor-pointer transition-all ${
                      isSelected
                        ? 'border-indigo-600 bg-indigo-50/70 ring-2 ring-indigo-500/20 shadow-xs'
                        : 'border-slate-200 bg-white hover:bg-slate-50'
                    }`}
                  >
                    <div className="flex items-start gap-2.5">
                      <input
                        type="radio"
                        name="select_one_option"
                        checked={isSelected}
                        onChange={() => setSelectedOptId(opt.id)}
                        className="mt-0.5 text-indigo-600 focus:ring-brand-primary"
                      />
                      <div className="min-w-0 flex-1">
                        <div className="text-xs font-extrabold text-slate-900">{opt.label}</div>
                        {opt.actionType === 'send_email' && (
                          <div className="mt-1 text-xs font-medium text-indigo-700 flex items-center gap-1">
                            <Send size={11} />
                            <span>Action: Sends Email template <strong>"{boundEmailName || opt.emailTemplateId || 'Selected Email'}"</strong></span>
                          </div>
                        )}
                        {opt.actionType === 'send_automation' && (
                          <div className="mt-1 text-xs font-medium text-indigo-700 flex items-center gap-1">
                            <Play size={11} />
                            <span>Action: Triggers Automation <strong>"{boundAutoName || opt.automationId || 'Selected Automation'}"</strong></span>
                          </div>
                        )}
                        {(!opt.actionType || opt.actionType === 'none') && (
                          <div className="mt-1 text-xs text-slate-500">
                            Action: Marks task complete without external send
                          </div>
                        )}
                      </div>
                    </div>
                  </label>
                );
              })}
            </div>
          </div>
        )}

        {/* 2. Yes / No Inputs */}
        {instance.actionType === 'yes_no' && (
          <div className="space-y-2">
            <label className="block text-xs font-extrabold text-slate-800 uppercase tracking-wider">
              Select Answer *
            </label>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setYesNoAnswer('yes')}
                className={`py-3 px-4 rounded-xl border font-bold text-xs cursor-pointer transition-all flex items-center justify-center gap-2 ${
                  yesNoAnswer === 'yes'
                    ? 'border-emerald-600 bg-emerald-50 text-emerald-800 ring-2 ring-emerald-500/20'
                    : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
                }`}
              >
                <CheckCircle2 size={15} />
                <span>{def?.config?.yesLabel || 'Yes'}</span>
              </button>

              <button
                type="button"
                onClick={() => setYesNoAnswer('no')}
                className={`py-3 px-4 rounded-xl border font-bold text-xs cursor-pointer transition-all flex items-center justify-center gap-2 ${
                  yesNoAnswer === 'no'
                    ? 'border-rose-600 bg-rose-50 text-rose-800 ring-2 ring-rose-500/20'
                    : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
                }`}
              >
                <X size={15} />
                <span>{def?.config?.noLabel || 'No'}</span>
              </button>
            </div>
          </div>
        )}

        {/* 3. Date Entry Inputs */}
        {instance.actionType === 'date_entry' && (
          <div className="space-y-2">
            <label className="block text-xs font-extrabold text-slate-800 uppercase tracking-wider">
              Select / Enter Date *
            </label>
            <input
              type="date"
              value={dateValue}
              onChange={(e) => setDateValue(e.target.value)}
              className="w-full px-3 py-2 rounded-xl border border-slate-200 text-[13px] font-bold text-slate-900 bg-white focus:ring-2 focus:ring-brand-primary outline-none"
            />
          </div>
        )}

        {/* 4. Notes Input (Mandatory or Optional) */}
        <div className="space-y-1.5 pt-2 border-t border-slate-100">
          <label className="block text-xs font-extrabold text-slate-800 flex items-center justify-between">
            <span>Admin Note {isRequireNote ? <span className="text-rose-600">* (Mandatory)</span> : <span className="text-slate-400 font-normal">(Optional)</span>}</span>
          </label>
          <textarea
            value={noteText}
            onChange={(e) => {
              setNoteText(e.target.value);
              if (validationError) setValidationError(null);
            }}
            rows={3}
            placeholder={isRequireNote ? 'Required: Enter notes regarding this action...' : 'Optional: Enter any notes...'}
            className="w-full px-3 py-2 rounded-xl border border-slate-200 text-[13px] text-slate-800 focus:outline-none focus:ring-2 focus:ring-brand-primary resize-y"
          />
        </div>
      </div>

      <div className="px-5 py-3 border-t border-slate-100 flex items-center justify-end gap-2 shrink-0 bg-slate-50">
        <button
          type="button"
          onClick={onCancel}
          disabled={confirming}
          className="px-4 py-2 rounded-xl border border-slate-200 text-slate-600 text-xs font-bold hover:bg-slate-100 disabled:opacity-50 cursor-pointer"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleConfirm}
          disabled={confirming}
          className="px-5 py-2 rounded-xl bg-brand-primary text-white text-xs font-extrabold hover:bg-brand-primary-dark disabled:opacity-50 cursor-pointer inline-flex items-center gap-1.5 shadow-xs"
        >
          {confirming && <Loader2 size={13} className="animate-spin" />}
          {confirming ? 'Executing...' : 'Confirm & Execute Action'}
        </button>
      </div>
    </ModalShell>
  );
}

// ── Controlled Rollback Modal ───────────────────────────────────────

export interface RollbackTaskModalProps {
  instance: ProgramPhaseActionInstance;
  reopening: boolean;
  onConfirmRollback: (reason: string) => void;
  onCancel: () => void;
}

export function RollbackTaskModal({
  instance,
  reopening,
  onConfirmRollback,
  onCancel,
}: RollbackTaskModalProps) {
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  const completedEntry = (instance.history || []).filter((h) => h.action === 'completed').pop();
  const executedEntry = (instance.history || []).filter((h) => h.action === 'executed').pop();

  const handleRollback = () => {
    if (!reason.trim()) {
      setError('A mandatory reason is required to perform a rollback.');
      return;
    }
    onConfirmRollback(reason.trim());
  };

  return (
    <ModalShell
      onClose={onCancel}
      labelId="rollback-task-modal-title"
      icon={<RotateCcw size={18} className="text-amber-600" />}
      title={`Rollback Task: ${instance.title}`}
      subtitle="Restore this task to an incomplete state for re-evaluation."
    >
      <div className="px-5 py-4 space-y-4 overflow-y-auto">
        {/* Historical Selection Details */}
        <div className="p-3.5 rounded-xl bg-slate-50 border border-slate-200 space-y-2 text-[13px]">
          <div className="font-extrabold text-slate-800">Historical Action Execution Record:</div>
          <div className="text-slate-600 space-y-1">
            <p><strong>Completed By:</strong> {instance.completedByName || 'Admin'}</p>
            {instance.completedAt && (
              <p><strong>Timestamp:</strong> {new Date(instance.completedAt).toLocaleString()}</p>
            )}
            {instance.selectedOptionLabel && (
              <p><strong>Option Chosen:</strong> {instance.selectedOptionLabel}</p>
            )}
            {instance.savedAnswer && (
              <p><strong>Answer Selected:</strong> {instance.savedAnswer.toUpperCase()}</p>
            )}
            {instance.savedDate && (
              <p><strong>Date Entered:</strong> {instance.savedDate}</p>
            )}
            {instance.savedText && (
              <p><strong>Note/Details:</strong> "{instance.savedText}"</p>
            )}
          </div>
        </div>

        {/* Irreversible Action Warning */}
        <div className="p-3.5 rounded-xl bg-amber-50 border border-amber-200 text-amber-900 text-[13px] space-y-1.5">
          <div className="flex items-center gap-2 font-extrabold text-amber-900">
            <AlertCircle size={15} className="shrink-0 text-amber-700" />
            <span>Controlled Rollback Notice</span>
          </div>
          <p className="text-xs leading-relaxed text-amber-800">
            Rolling back restores the task to 'reopened' status. Note that external actions such as sent emails or executed external webhooks cannot be unsent. Historical completion logs will remain permanently recorded in the audit trail.
          </p>
        </div>

        {error && (
          <div className="p-3 rounded-xl bg-rose-50 border border-rose-200 text-[13px] font-semibold text-rose-700">
            {error}
          </div>
        )}

        {/* Reason Input */}
        <div className="space-y-1.5">
          <label className="block text-xs font-extrabold text-slate-800">
            Reason for Rollback * (Mandatory)
          </label>
          <textarea
            value={reason}
            onChange={(e) => {
              setReason(e.target.value);
              if (error) setError(null);
            }}
            rows={3}
            autoFocus
            placeholder="Please enter why this task selection is being rolled back..."
            className="w-full px-3 py-2 rounded-xl border border-slate-200 text-[13px] text-slate-800 focus:outline-none focus:ring-2 focus:ring-amber-500 resize-y"
          />
        </div>
      </div>

      <div className="px-5 py-3 border-t border-slate-100 flex items-center justify-end gap-2 shrink-0 bg-slate-50">
        <button
          type="button"
          onClick={onCancel}
          disabled={reopening}
          className="px-4 py-2 rounded-xl border border-slate-200 text-slate-600 text-xs font-bold hover:bg-slate-100 disabled:opacity-50 cursor-pointer"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleRollback}
          disabled={reopening || !reason.trim()}
          className="px-5 py-2 rounded-xl bg-amber-600 text-white text-xs font-extrabold hover:bg-amber-700 disabled:opacity-50 cursor-pointer inline-flex items-center gap-1.5 shadow-xs"
        >
          {reopening && <Loader2 size={13} className="animate-spin" />}
          {reopening ? 'Rolling back...' : 'Confirm Rollback'}
        </button>
      </div>
    </ModalShell>
  );
}

// ── 3.3 — Content viewing ───────────────────────────────────────────

export interface ViewFileRef {
  name: string;
  /** Direct URL (download/preview URL), when known. */
  url?: string;
  /** Firebase Storage path, when known — served via DocPreviewModal. */
  storagePath?: string;
}

function fileKind(name: string): 'pdf' | 'image' | 'other' {
  const lower = (name || '').toLowerCase();
  if (/\.pdf($|\?)/.test(lower)) return 'pdf';
  if (/\.(png|jpe?g|gif|webp|bmp|svg)($|\?)/.test(lower)) return 'image';
  return 'other';
}

export function ContentViewModal({
  title,
  text,
  file,
  onClose,
}: {
  title: string;
  text?: string;
  file?: ViewFileRef;
  onClose: () => void;
}) {
  const [pathPreviewOpen, setPathPreviewOpen] = useState(false);
  const kind = file ? fileKind(file.name || file.url || '') : null;
  const usePathPreview = !!file?.storagePath && !file?.url;

  return (
    <>
      <ModalShell
        onClose={onClose}
        labelId="phase-action-view-title"
        icon={<Eye size={17} className="text-indigo-600" />}
        title={title}
        wide={!!file}
      >
        <div className="px-5 py-4 overflow-y-auto">
          {typeof text === 'string' && (
            <div className="text-[13px] leading-relaxed text-slate-700 whitespace-pre-wrap break-words">{text}</div>
          )}
          {file && (
            <div>
              {file.url && kind === 'image' && (
                <img src={file.url} alt={file.name} className="max-w-full max-h-[60vh] mx-auto rounded-lg border border-slate-200" />
              )}
              {file.url && kind === 'pdf' && (
                <iframe title={file.name} src={file.url} className="w-full h-[60vh] rounded-lg border border-slate-200" />
              )}
              {file.url && kind === 'other' && (
                <p className="text-[13px] text-slate-600">
                  This file type cannot be previewed here.{' '}
                  <a href={file.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 font-bold text-indigo-700 hover:text-indigo-900">
                    <Download size={12} /> Download {file.name}
                  </a>
                </p>
              )}
              {!file.url && usePathPreview && (
                <button
                  type="button"
                  onClick={() => setPathPreviewOpen(true)}
                  className="px-3.5 py-2 rounded-xl bg-brand-primary text-white text-xs font-bold hover:bg-brand-primary-dark cursor-pointer inline-flex items-center gap-1.5"
                >
                  <Eye size={13} /> Open {file.name}
                </button>
              )}
              {!file.url && !usePathPreview && <p className="text-[13px] text-slate-500">No file is attached to this task.</p>}
              {file.url && (
                <p className="mt-3 text-xs text-slate-500">
                  {file.name} ·{' '}
                  <a href={file.url} target="_blank" rel="noreferrer" className="font-semibold text-indigo-600 hover:text-indigo-800">
                    Open in a new tab
                  </a>
                </p>
              )}
            </div>
          )}
        </div>
      </ModalShell>
      {pathPreviewOpen && file?.storagePath && (
        <DocPreviewModal open onClose={() => setPathPreviewOpen(false)} storagePath={file.storagePath} fileName={file.name} />
      )}
    </>
  );
}

// ── 3.3 — Record (text_input) note editor ───────────────────────────

export function NoteEditModal({
  taskTitle,
  initialText,
  saving,
  onSave,
  onCancel,
}: {
  taskTitle: string;
  initialText: string;
  saving: boolean;
  onSave: (text: string) => void;
  onCancel: () => void;
}) {
  const [text, setText] = useState(initialText);
  useEffect(() => {
    setText(initialText);
  }, [initialText, taskTitle]);

  return (
    <ModalShell
      onClose={onCancel}
      labelId="phase-action-note-title"
      icon={<StickyNote size={17} className="text-indigo-600" />}
      title={taskTitle}
      subtitle="The note is saved on the application record and completes this task."
    >
      <div className="px-5 py-4">
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={6}
          autoFocus
          placeholder="Write the note…"
          className="w-full px-3 py-2 rounded-xl border border-slate-200 text-[13px] leading-relaxed text-slate-800 focus:outline-none focus:ring-2 focus:ring-brand-primary resize-y"
        />
      </div>
      <div className="px-5 py-3 border-t border-slate-100 flex items-center justify-end gap-2 shrink-0">
        <button
          type="button"
          onClick={onCancel}
          disabled={saving}
          className="px-3.5 py-2 rounded-xl border border-slate-200 text-slate-600 text-xs font-bold hover:bg-slate-50 disabled:opacity-50 cursor-pointer"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={() => onSave(text)}
          disabled={saving || !text.trim()}
          className="px-4 py-2 rounded-xl bg-brand-primary text-white text-xs font-bold hover:bg-brand-primary-dark disabled:opacity-50 cursor-pointer inline-flex items-center gap-1.5"
        >
          {saving && <Loader2 size={13} className="animate-spin" />}
          {saving ? 'Saving…' : 'Save note'}
        </button>
      </div>
    </ModalShell>
  );
}
